// frontend/src/components/reports/ReportSubmission.tsx
import { useParams, useNavigate, useBlocker } from 'react-router-dom';
import {
    Container,
    Title,
    Paper,
    Stack,
    Group,
    Text,
    Stepper,
    Alert,
    TextInput,
    NumberInput,
    Select,
    MultiSelect,
    Textarea,
    Tooltip,
    Skeleton,
    Divider,
    Switch,
    ScrollArea,
    Menu,
    ActionIcon
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import {
  IconAlertCircle,
  IconInfoCircle,
  IconDownload,
  IconDots,
  IconCircleCheck,
  IconClipboardList,
  IconArrowBackUp
} from '@tabler/icons-react';
import { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { Document, Page, Text as PDFText, View as PDFView, pdf } from '@react-pdf/renderer';
import { useGroupQuery } from '../../hooks/useGroupQuery';
import { reportsApi } from '../../api/reports';
import { ReportQuestion, ReportSection, ReportAnswer, getReportDisplayTitle } from '../../types/reports';
import { ActionButton } from '../common/ActionButton';
import { ConfirmationModal } from '../common/ConfirmationModal';
import { StatusBadge } from '../common/StatusBadge';
import { mapReportStatus } from '../../utils/statusUtils';
import { showSuccessNotification, showErrorNotification, showApiErrorNotification } from '../../utils/notifications';
import { formatTimestampToLocal } from '../../utils/dateUtils';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { FileManagement } from './FileManagement';
import { AdminReviewModals } from './AdminReviewModals';
import { useAuthStore } from '../../stores/authStore';
import { buildPdfRenderableQuestions } from './pdfRenderableQuestions';



interface FormValues {
  [key: string]: any;
}

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseDateInputValue = (value: string): Date | null => {
  const dateOnlyMatch = value.match(DATE_ONLY_PATTERN);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateForApi = (date: Date): string => dayjs(date).format('YYYY-MM-DD');

const shouldShowQuestionForValues = (question: ReportQuestion, values: FormValues): boolean => {
  if (!question.conditional_logic?.show_if) return true;

  const { question_id, operator, value } = question.conditional_logic.show_if;
  const dependentValue = values[`question_${question_id}`];

  switch (operator) {
    case 'greater_than':
      return Number(dependentValue) > Number(value);
    case 'less_than':
      return Number(dependentValue) < Number(value);
    case 'equals':
      if (typeof value === 'string') {
        return dependentValue === value;
      }
      return Number(dependentValue) === value;
    case 'not_equals':
      if (typeof value === 'string') {
        return dependentValue !== value;
      }
      return Number(dependentValue) !== value;
    case 'contains':
      if (Array.isArray(dependentValue)) {
        return dependentValue.includes(value);
      }
      return String(dependentValue).includes(String(value));
    default:
      return true;
  }
};

const getHiddenShowIfQuestionIds = (values: FormValues, questions: ReportQuestion[]): Set<number> => {
  const hiddenIds = new Set<number>();

  questions.forEach((question) => {
    if (question.conditional_logic?.show_if && !shouldShowQuestionForValues(question, values)) {
      hiddenIds.add(question.id);
    }
  });

  return hiddenIds;
};

export function ReportSubmission() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAdmin = useAuthStore((s) => s.hasRole('admin'));
  const [active, setActive] = useState(0);
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isSavingAndExiting, setIsSavingAndExiting] = useState(false);
  const [isEditingCurrentSection, setIsEditingCurrentSection] = useState(false);

  const [isFullReportView, setIsFullReportView] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [adminStartReviewModalOpen, setAdminStartReviewModalOpen] = useState(false);
  const [adminApproveModalOpen, setAdminApproveModalOpen] = useState(false);
  const [adminRejectModalOpen, setAdminRejectModalOpen] = useState(false);

  // Fetch report report and data
  const { data: report, isLoading, error: queryError } = useGroupQuery(
    ['report', reportId],
    () => reportsApi.getReport(reportId || ''),
    { enabled: !!reportId }
  );

  // Save answers mutation
  const saveAnswersMutation = useMutation({
    mutationFn: (answers: ReportAnswer[]) => reportsApi.saveAnswers(reportId || '', answers),
    onSuccess: () => {
      showSuccessNotification('Your progress has been saved');
      // Invalidate report query to refresh data
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
    },
    onError: (error) => {
      showApiErrorNotification(error, 'Failed to save progress');
    }
  });

  // Update report status mutation
  const updateStatusMutation = useMutation({
    mutationFn: (status: string) => reportsApi.updateReportStatus(reportId || '', status as any),
    onSuccess: () => {
      // Invalidate queries to refresh the reports list with updated status
      queryClient.invalidateQueries({ queryKey: ['recent-reports'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
    },
    onError: (error) => {
      showApiErrorNotification(error, 'Failed to update report status');
    }
  });

  const handleAdminStartReview = async (): Promise<void> => {
    await updateStatusMutation.mutateAsync('in_review');
    showSuccessNotification('Report is now in review');
  };

  const handleAdminApprove = async (): Promise<void> => {
    await updateStatusMutation.mutateAsync('approved');
    showSuccessNotification('Report approved');
  };

  const handleAdminReject = async (): Promise<void> => {
    await updateStatusMutation.mutateAsync('rejected');
    showSuccessNotification('Report rejected');
  };

  // Helper function to find question number by ID for validation messages
  // Numbers questions based on their order in sections, only counting main questions (non-repeating, non-sub-questions)
  const getQuestionNumber = (questionId: number): number => {
    if (!report?.template?.sections || !report?.template?.questions) return questionId;
    
    let questionNumber = 1;
    
    // Go through sections in order
    for (const section of report.template.sections) {
      // Go through question IDs in section order
      for (const qId of section.question_ids) {
        const question = report.template.questions.find(q => q.id === qId);
        // Only count main questions (not repeat-based and not sub-questions)
        if (question && 
            !question.conditional_logic?.repeat_based_on && 
            !question.validation?.max_value_from_question) {
          if (question.id === questionId) {
            return questionNumber;
          }
          questionNumber++;
        }
      }
    }
    
    return questionId; // fallback if not found
  };

  const isExplicitZeroValue = (value: any): boolean => {
    if (value === 0) return true;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed !== '' && Number(trimmed) === 0;
    }
    return false;
  };

  const getNumericValue = (value: any): number | null => {
    if (value === undefined || value === null || value === '') return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };

  const formatDisplayNumber = (value: number): string => {
    if (Number.isInteger(value)) return value.toString();
    return value.toFixed(2).replace(/\.?0+$/, '');
  };

  const getChildSumValidationErrors = (
    values: FormValues,
    questions: ReportQuestion[]
  ): Record<string, string> => {
    const errors: Record<string, string> = {};
    const parentToChildren = new Map<number, ReportQuestion[]>();

    questions.forEach((question) => {
      const parentId = question.validation?.max_value_from_question;
      if (parentId === undefined) return;

      const children = parentToChildren.get(parentId) || [];
      children.push(question);
      parentToChildren.set(parentId, children);
    });

    parentToChildren.forEach((children, parentId) => {
      const parentFieldName = `question_${parentId}`;
      const parentNumericValue = getNumericValue(values[parentFieldName]);

      if (parentNumericValue === null) return;

      let childTotal = 0;

      children.forEach((childQuestion) => {
        if (childQuestion.conditional_logic?.repeat_based_on) {
          const repeatControllerId = childQuestion.conditional_logic.repeat_based_on;
          const repeatCount = Number(values[`question_${repeatControllerId}`]) || 0;

          for (let index = 0; index < repeatCount; index++) {
            const childValue = getNumericValue(values[`question_${childQuestion.id}_${index}`]);
            childTotal += childValue ?? 0;
          }
          return;
        }

        const childValue = getNumericValue(values[`question_${childQuestion.id}`]);
        childTotal += childValue ?? 0;
      });

      if (Math.abs(childTotal - parentNumericValue) > 0.000001) {
        const parentQuestionNumber = getQuestionNumber(parentId);
        errors[parentFieldName] = `Child values must sum to ${formatDisplayNumber(parentNumericValue)} for Question ${parentQuestionNumber} (currently ${formatDisplayNumber(childTotal)})`;
      }
    });

    return errors;
  };

  const getQuestionValidationErrors = (
    values: FormValues,
    questions: ReportQuestion[],
    visibleOnly: boolean
  ): Record<string, string> => {
    const errors: Record<string, string> = {};

    questions.forEach((question) => {
      if (visibleOnly && !shouldShowQuestionForValues(question, values)) {
        return;
      }

      if (question.conditional_logic?.repeat_based_on) {
        const repeatCount = Number(values[`question_${question.conditional_logic.repeat_based_on}`]) || 0;

        for (let i = 0; i < repeatCount; i++) {
          const fieldName = `question_${question.id}_${i}`;
          const value = values[fieldName];

          if (question.required && (value === undefined || value === null || value === '')) {
            errors[fieldName] = 'This field is required';
          }

          if ((question.type === 'Number' || question.type === 'Currency') &&
              value !== '' && value !== null && value !== undefined) {
            const numValue = Number(value);

            if (question.validation?.min_value !== undefined && numValue < question.validation.min_value) {
              errors[fieldName] = `Value must be at least ${question.validation.min_value}`;
            }
            if (question.validation?.max_value !== undefined && numValue > question.validation.max_value) {
              errors[fieldName] = `Value must be at most ${question.validation.max_value}`;
            }

            if (question.validation?.max_value_from_question !== undefined) {
              const referencedQuestionId = question.validation.max_value_from_question;
              const referencedValue = values[`question_${referencedQuestionId}`];
              const maxValueFromQuestion = Number(referencedValue) || 0;

              if (numValue > maxValueFromQuestion) {
                const referencedQuestionNumber = getQuestionNumber(referencedQuestionId);
                errors[fieldName] = `Value cannot exceed ${maxValueFromQuestion} (from Question ${referencedQuestionNumber})`;
              }
            }
          }
        }

        return;
      }

      const fieldName = `question_${question.id}`;
      const value = values[fieldName];

      if (question.required && (value === undefined || value === null || value === '')) {
        errors[fieldName] = 'This field is required';
      }

      if ((question.type === 'Number' || question.type === 'Currency') &&
          value !== '' && value !== null && value !== undefined) {
        const numValue = Number(value);

        if (question.validation?.min_value !== undefined && numValue < question.validation.min_value) {
          errors[fieldName] = `Value must be at least ${question.validation.min_value}`;
        }
        if (question.validation?.max_value !== undefined && numValue > question.validation.max_value) {
          errors[fieldName] = `Value must be at most ${question.validation.max_value}`;
        }

        if (question.validation?.max_value_from_question !== undefined) {
          const referencedQuestionId = question.validation.max_value_from_question;
          const referencedValue = values[`question_${referencedQuestionId}`];
          const maxValueFromQuestion = Number(referencedValue) || 0;

          if (numValue > maxValueFromQuestion) {
            const referencedQuestionNumber = getQuestionNumber(referencedQuestionId);
            errors[fieldName] = `Value cannot exceed ${maxValueFromQuestion} (from Question ${referencedQuestionNumber})`;
          }
        }
      }
    });

    const childSumErrors = getChildSumValidationErrors(values, questions);
    Object.assign(errors, childSumErrors);

    return errors;
  };

  const form = useForm<FormValues>({
    initialValues: {},
    validate: (values) => {
      return getQuestionValidationErrors(values, report?.template?.questions || [], false);
    }
  });

  // Helper function to get input props with proper defaults
  const getInputPropsWithDefaults = (fieldName: string, questionType?: string) => {
    const baseProps = form.getInputProps(fieldName);
    
    // Ensure value is never undefined to prevent controlled/uncontrolled warnings
    if (baseProps.value === undefined) {
      if (questionType === 'MultiSelect') {
        baseProps.value = [];
      } else if (questionType === 'Date') {
        baseProps.value = null;
      } else {
        baseProps.value = '';
      }
    }
    
    // Convert string dates to Date objects for DateInput
    if (questionType === 'Date' && baseProps.value && typeof baseProps.value === 'string') {
      try {
        baseProps.value = parseDateInputValue(baseProps.value);
      } catch (e) {
        baseProps.value = null;
      }
    }
    
    return baseProps;
  };

  // Set form values when report data is loaded
  useEffect(() => {
    if (report?.answers) {
      const initialValues = report.answers.reduce((acc, answer) => {
        if (answer.repeat_index !== null && answer.repeat_index !== undefined) {
          acc[`question_${answer.question_id}_${answer.repeat_index}`] = answer.value;
        } else {
          acc[`question_${answer.question_id}`] = answer.value;
        }
        return acc;
      }, {} as Record<string, any>);

      form.setValues(initialValues);
      if (isInitialLoad) {
        form.resetDirty();
        setIsInitialLoad(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.answers, isInitialLoad]);

  // Clear child values when their max_value_from_question parent is explicitly set to zero.
  // This prevents stale child answers from being persisted after a parent is zeroed out.
  useEffect(() => {
    if (!report?.template?.questions?.length) return;

    const childQuestionIdsToClear = new Set<number>();

    report.template.questions.forEach((question) => {
      const parentId = question.validation?.max_value_from_question;
      if (parentId === undefined) return;

      const parentValue = form.values[`question_${parentId}`];
      if (isExplicitZeroValue(parentValue)) {
        childQuestionIdsToClear.add(question.id);
      }
    });

    if (childQuestionIdsToClear.size === 0) return;

    const updates: Record<string, any> = {};
    const nextErrors: Record<string, string> = {};
    let hasErrorUpdates = false;

    Object.entries(form.errors).forEach(([key, value]) => {
      nextErrors[key] = String(value);
    });

    Object.entries(form.values).forEach(([key, value]) => {
      const match = key.match(/^question_(\d+)(?:_\d+)?$/);
      if (!match) return;

      const questionId = Number(match[1]);
      if (!childQuestionIdsToClear.has(questionId)) return;

      if (value !== undefined && value !== null && value !== '') {
        updates[key] = '';
      }

      if (nextErrors[key]) {
        delete nextErrors[key];
        hasErrorUpdates = true;
      }
    });

    if (Object.keys(updates).length > 0) {
      form.setValues({ ...form.values, ...updates });
    }

    if (hasErrorUpdates) {
      form.setErrors(nextErrors);
    }
  }, [form, form.values, form.errors, report?.template?.questions]);

  // Clear values for all questions hidden by show_if to prevent stale hidden answers from persisting.
  useEffect(() => {
    if (!report?.template?.questions?.length) return;

    const hiddenQuestionIds = getHiddenShowIfQuestionIds(form.values, report.template.questions);
    if (hiddenQuestionIds.size === 0) return;

    const updates: Record<string, any> = {};
    const nextErrors: Record<string, string> = {};
    let hasErrorUpdates = false;

    Object.entries(form.errors).forEach(([key, value]) => {
      nextErrors[key] = String(value);
    });

    Object.entries(form.values).forEach(([key, value]) => {
      const match = key.match(/^question_(\d+)(?:_\d+)?$/);
      if (!match) return;

      const questionId = Number(match[1]);
      if (!hiddenQuestionIds.has(questionId)) return;

      if (value !== undefined && value !== null && value !== '') {
        updates[key] = '';
      }

      if (nextErrors[key]) {
        delete nextErrors[key];
        hasErrorUpdates = true;
      }
    });

    if (Object.keys(updates).length > 0) {
      form.setValues({ ...form.values, ...updates });
    }

    if (hasErrorUpdates) {
      form.setErrors(nextErrors);
    }
  }, [form, form.values, form.errors, report?.template?.questions]);

  // Show error notification when query fails
  useEffect(() => {
    if (queryError) {
      const errorMessage = queryError instanceof Error ? queryError.message : 'Failed to load report';
      showErrorNotification(errorMessage);
    }
  }, [queryError]);

  // Check if form values have actually changed from initial values
  const hasFormChanges = useMemo(() => {
    if (!report?.answers) return false;

    const initialValues = report.answers.reduce((acc, answer) => {
      if (answer.repeat_index !== null && answer.repeat_index !== undefined) {
        acc[`question_${answer.question_id}_${answer.repeat_index}`] = answer.value;
      } else {
        acc[`question_${answer.question_id}`] = answer.value;
      }
      return acc;
    }, {} as Record<string, any>);

    // Only compare form values that currently exist in the form
    // This avoids false positives from questions in other sections or hidden by conditional logic
    const formValueKeys = Object.keys(form.values);


    // Check if any current form values have changed from their initial state
    const hasChanges = formValueKeys.some(key => {
      const initialValue = initialValues[key];
      const currentValue = form.values[key];
      
      // Normalize undefined/null/empty string values to null for consistent comparison
      const normalizeValue = (value: any) => {
        if (value === undefined || value === null || value === '') {
          return null;
        }
        return value;
      };
      
      const normalizedInitial = normalizeValue(initialValue);
      const normalizedCurrent = normalizeValue(currentValue);
      
      // Handle arrays (for MultiSelect)
      if (Array.isArray(normalizedInitial) && Array.isArray(normalizedCurrent)) {
        if (normalizedInitial.length !== normalizedCurrent.length) return true;
        return JSON.stringify(normalizedInitial.sort()) !== JSON.stringify(normalizedCurrent.sort());
      }
      
      // Handle case where one is array and other is not
      if (Array.isArray(normalizedInitial) !== Array.isArray(normalizedCurrent)) {
        return true;
      }
      
      // Handle other types with direct comparison (more reliable than JSON.stringify)
      const isChanged = normalizedInitial !== normalizedCurrent;

      return isChanged;
    });

    return hasChanges;
  }, [form.values, report?.answers]);

  const shouldShowQuestion = (question: ReportQuestion): boolean => {
    return shouldShowQuestionForValues(question, form.values);
  };

  const isReadOnly = report?.submission_status.state === 'approved' || report?.submission_status.state === 'in_review' || report?.submission_status.state === 'pending_review';
  
  // For pending_review status, allow editing current section only when in edit mode
  const isCurrentSectionReadOnly = isReadOnly && !(report?.submission_status.state === 'pending_review' && isEditingCurrentSection);

  const formatValue = (value: any, type: string) => {
    if (value === undefined || value === null || value === '') {
      return 'Not provided';
    }

    if (type === 'Number') {
      return new Intl.NumberFormat('en-US').format(Number(value));
    }

    if (type === 'Currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(Number(value));
    }

    if (type === 'MultiSelect' && Array.isArray(value)) {
      return value.join(', ');
    }

    if (type === 'Date') {
      try {
        const d = dayjs(value);
        if (!d.isValid()) return value;
        return d.format('MMMM D, YYYY');
      } catch (e) {
        return value;
      }
    }

    return value;
  };

  const numberFormatter = (value: string) => {
    if (!value) return '';
    return new Intl.NumberFormat('en-US').format(Number(value));
  };

  const currencyFormatter = (value: string) => {
    if (!value) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Number(value));
  };

  const numberParser = (value: string) => {
    if (!value) return '';
    return value.replace(/\$\s?|(,*)/g, '');
  };

  const currencyParser = (value: string) => {
    if (!value) return '';
    return value.replace(/[^0-9.-]+/g, '');
  };

  const MAX_REPEAT_COUNT = 20; // Maximum number of repeating questions allowed

  // Helper function to convert numbers to ordinal words
  const getOrdinalWord = (num: number): string => {
    const ordinals = [
      'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth',
      'eleventh', 'twelfth', 'thirteenth', 'fourteenth', 'fifteenth', 'sixteenth', 'seventeenth', 
      'eighteenth', 'nineteenth', 'twentieth'
    ];
    
    // Return the ordinal word if within our array, otherwise fall back to number format
    return ordinals[num - 1] || `${num}${getOrdinalSuffix(num)}`;
  };

  // Helper function to get ordinal suffix for numbers beyond our word list
  const getOrdinalSuffix = (num: number): string => {
    const lastDigit = num % 10;
    const lastTwoDigits = num % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
      return 'th';
    }
    
    switch (lastDigit) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Helper function to check if a question is a sub-question (has max_value_from_question or repeat_based_on)
  const isSubQuestion = (question: ReportQuestion): boolean => {
    return question.validation?.max_value_from_question !== undefined || 
           question.conditional_logic?.repeat_based_on !== undefined;
  };

  const renderQuestion = (question: ReportQuestion) => {
    if (!shouldShowQuestion(question)) return null;

    // Get the question number for display
    const questionNumber = getQuestionNumber(question.id);
    
    // Check if this is a sub-question (for indentation)
    const isSubQ = isSubQuestion(question);
    // Check if this is specifically a max_value_from_question (for border)
    const shouldShowBorder = question.validation?.max_value_from_question !== undefined;

    // Check if this question should have a number (main questions only)
    const shouldShowNumber = !question.conditional_logic?.repeat_based_on && !question.validation?.max_value_from_question;

    // Handle repeat_based_on logic
    if (question.conditional_logic?.repeat_based_on) {
      const repeatCount = Number(form.values[`question_${question.conditional_logic.repeat_based_on}`]) || 0;
      return (
        <Stack
          key={question.id}
          spacing="md"
          sx={(theme) => ({
            marginLeft: isSubQ ? '24px' : '0px',
            position: 'relative',
            paddingLeft: isSubQ ? '16px' : '0px',
            borderLeft: shouldShowBorder ? `2px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4]}` : 'none',
            '&::before': shouldShowBorder ? {
              content: '""',
              position: 'absolute',
              left: '-8px',
              top: '20px',
              width: '14px',
              height: '2px',
              backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4],
              pointerEvents: 'none',
              zIndex: 1,
            } : {}
          })}
        >
          {Array.from({ length: repeatCount }, (_, index) => {
            const fieldName = `question_${question.id}_${index}`;
            const value = form.values[fieldName];

            if (isCurrentSectionReadOnly) {
              return (
                <Stack key={`${question.id}_${index}`} spacing={4}>
                  <Text size="sm" color="dimmed">
                    {question.conditional_logic?.substitute_index 
                      ? question.text.replace('[first]', getOrdinalWord(index + 1))
                      : `${question.text} (${index + 1})`}
                  </Text>
                  <Text>{formatValue(value, question.type)}</Text>
                </Stack>
              );
            }

            const commonProps = {
              label: question.conditional_logic?.substitute_index 
                ? question.text.replace('[first]', getOrdinalWord(index + 1))
                : `${question.text} (${index + 1})`,
              required: question.required,
              error: form.errors[fieldName],
              ...getInputPropsWithDefaults(fieldName, question.type),
              styles: {
                label: {
                  marginBottom: '8px'
                }
              }
            };

            switch (question.type) {
              case 'Text':
                return question.validation?.multiline ? (
                  <Textarea
                    key={`${question.id}_${index}`}
                    {...commonProps}
                    minRows={3}
                    maxRows={6}
                  />
                ) : (
                  <TextInput
                    key={`${question.id}_${index}`}
                    {...commonProps}
                  />
                );
              case 'Number':
                return (
                  <NumberInput
                    key={`${question.id}_${index}`}
                    {...commonProps}
                    min={question.validation?.min_value}
                    hideControls
                    formatter={numberFormatter}
                    parser={numberParser}
                    styles={{
                      root: { width: '100%', marginBottom: '8px' },
                      input: { width: '300px' }
                    }}
                  />
                );
              case 'Currency':
                return (
                  <NumberInput
                    key={`${question.id}_${index}`}
                    {...commonProps}
                    min={question.validation?.min_value}
                    hideControls
                    formatter={currencyFormatter}
                    parser={currencyParser}
                    styles={{
                      root: { width: '100%', marginBottom: '8px' },
                      input: { width: '300px' }
                    }}
                  />
                );
              case 'SingleSelect':
                return (
                  <Select
                    key={`${question.id}_${index}`}
                    {...commonProps}
                    data={question.options || []}
                    clearable
                    styles={{
                      root: { width: '300px' },
                      label: {
                        marginBottom: '8px'
                      }
                    }}
                  />
                );
              case 'MultiSelect':
                return (
                  <MultiSelect
                    key={`${question.id}_${index}`}
                    {...commonProps}
                    data={question.options || []}
                    searchable
                    creatable={question.allow_other}
                    maxSelectedValues={question.validation?.max_selections}
                    styles={{
                      root: { width: '300px' },
                      label: {
                        marginBottom: '8px'
                      }
                    }}
                  />
                );
              case 'Date':
                return (
                  <DateInput
                    key={`${question.id}_${index}`}
                    {...commonProps}
                    placeholder="Select a date"
                    weekendDays={[]}
                    styles={{
                      root: { width: '300px' },
                      label: {
                        marginBottom: '8px'
                      }
                    }}
                  />
                );
              default:
                return null;
            }
          })}
        </Stack>
      );
    }

    // Regular question rendering (non-repeating)
    const fieldName = `question_${question.id}`;
    const value = form.values[fieldName];

    if (isCurrentSectionReadOnly) {
      return (
        <Stack
          key={question.id}
          spacing="md"
          sx={(theme) => ({
            marginLeft: isSubQ ? '24px' : '0px',
            position: 'relative',
            paddingLeft: isSubQ ? '16px' : '0px',
            borderLeft: shouldShowBorder ? `2px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4]}` : 'none',
            '&::before': shouldShowBorder ? {
              content: '""',
              position: 'absolute',
              left: '-8px',
              top: '20px',
              width: '14px',
              height: '2px',
              backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4],
              pointerEvents: 'none',
              zIndex: 1,
            } : {}
          })}
        >
          <Stack spacing={4}>
            <Text size="sm" color="dimmed">
              {shouldShowNumber ? `${questionNumber}. ${question.text}` : question.text}
            </Text>
            <Text>{formatValue(value, question.type)}</Text>
          </Stack>
        </Stack>
      );
    }

    const commonProps = {
      label: shouldShowNumber ? `${questionNumber}. ${question.text}` : question.text,
      required: question.required,
      error: form.errors[fieldName],
      ...getInputPropsWithDefaults(fieldName, question.type),
      styles: {
        label: {
          marginBottom: '8px'
        }
      }
    };

    const questionComponent = (() => {
      switch (question.type) {
        case 'Text':
          return question.validation?.multiline ? (
            <Textarea
              key={question.id}
              {...commonProps}
              minRows={3}
              maxRows={6}
            />
          ) : (
            <TextInput
              key={question.id}
              {...commonProps}
            />
          );
        case 'Number':
          return (
            <NumberInput
              key={question.id}
              {...commonProps}
              min={question.validation?.min_value}
              hideControls
              formatter={numberFormatter}
              parser={numberParser}
              styles={{
                root: { width: '100%' },
                input: { width: '300px' },
                label: {
                  marginBottom: '8px'
                }
              }}
            />
          );
        case 'Currency':
          return (
            <NumberInput
              key={question.id}
              {...commonProps}
              min={question.validation?.min_value}
              hideControls
              formatter={currencyFormatter}
              parser={currencyParser}
              styles={{
                root: { width: '100%' },
                input: { width: '300px' },
                label: {
                  marginBottom: '8px'
                }
              }}
            />
          );
        case 'SingleSelect':
          return (
            <Select
              key={question.id}
              {...commonProps}
              data={question.options || []}
              clearable
              styles={{
                root: { width: '300px' },
                label: {
                  marginBottom: '8px'
                }
              }}
            />
          );
        case 'MultiSelect':
          return (
            <MultiSelect
              key={question.id}
              {...commonProps}
              data={question.options || []}
              searchable
              creatable={question.allow_other}
              maxSelectedValues={question.validation?.max_selections}
              styles={{
                root: { width: '300px' },
                label: {
                  marginBottom: '8px'
                }
              }}
            />
          );
        case 'Date':
          return (
            <DateInput
              key={question.id}
              {...commonProps}
              placeholder="Select a date"
              weekendDays={[]}
              styles={{
                root: { width: '300px' },
                label: {
                  marginBottom: '8px'
                }
              }}
            />
          );
        default:
          return null;
      }
    })();

    return (
      <Stack
        key={question.id}
        spacing="md"
        sx={(theme) => ({
          marginLeft: isSubQ ? '24px' : '0px',
          position: 'relative',
          paddingLeft: isSubQ ? '16px' : '0px',
          borderLeft: shouldShowBorder ? `2px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4]}` : 'none',
          '&::before': shouldShowBorder ? {
            content: '""',
            position: 'absolute',
            left: '-8px',
            top: '20px',
            width: '14px',
            height: '2px',
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4],
            pointerEvents: 'none',
            zIndex: 1,
          } : {}
        })}
      >
        {questionComponent}
      </Stack>
    );
  };

  const renderSection = (sectionId: string, hideHeader = false) => {
    if (!report?.template?.sections) return null;
    
    const section = report.template.sections.find(s => s.id === sectionId);
    if (!section) return null;

    // Get questions in the order defined by section.question_ids
    const sectionQuestions = section.question_ids
      .map(qId => report.template.questions.find((q: ReportQuestion) => q.id === qId))
      .filter((q): q is ReportQuestion => q !== undefined);

    // Track which questions have been rendered to avoid duplicates
    const renderedQuestions = new Set<number>();

    return (
      <Stack key={section.id} spacing="md">
        {!hideHeader && (
          <>
            <Title order={3}>{section.title}</Title>
            <Text color="dimmed">{section.description}</Text>
          </>
        )}
        
        {/* Render questions in order, with repeating sections immediately following their controllers */}
        {sectionQuestions.map((question: ReportQuestion) => {
          if (renderedQuestions.has(question.id)) return null;
          
          // Check if this question controls repeating questions
          const repeatingQuestions = sectionQuestions.filter(q => 
            q.conditional_logic?.repeat_based_on === question.id
          );
          
          if (repeatingQuestions.length > 0) {
            // This question controls repeating questions
            renderedQuestions.add(question.id);
            repeatingQuestions.forEach(q => renderedQuestions.add(q.id));
            
            const rawRepeatCount = Number(form.values[`question_${question.id}`]) || 0;
            const repeatCount = Math.min(Math.max(0, rawRepeatCount), MAX_REPEAT_COUNT);
            
            // If the input value is greater than our max, update the form value
            if (rawRepeatCount > MAX_REPEAT_COUNT) {
              form.setFieldValue(`question_${question.id}`, MAX_REPEAT_COUNT);
            }

            return (
              <Stack key={question.id} spacing="md">
                {/* Render the controlling question */}
                {renderQuestion(question)}
                
                {/* Disclaimer for conditional questions */}
                {!isCurrentSectionReadOnly && (
                  <Alert color="blue" variant="light" p="sm">
                    <Text size="sm">
                      <strong>Note:</strong> You will need to answer {repeatingQuestions.length} question{repeatingQuestions.length > 1 ? 's' : ''} for each item you specify above.
                    </Text>
                  </Alert>
                )}
                
                {/* Render repeating questions immediately below if count > 0 */}
                {repeatCount > 0 && (
                  <Stack 
                    spacing="md"
                    sx={{
                      marginLeft: '24px',
                      paddingLeft: '16px'
                    }}
                  >
                    {Array.from({ length: repeatCount }, (_, index) => (
                      <Stack key={index} spacing="md" p="md" sx={(theme) => ({
                        border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
                        borderRadius: '4px'
                      })}>
                        {repeatingQuestions.map((repeatingQuestion: ReportQuestion) => {
                          const fieldName = `question_${repeatingQuestion.id}_${index}`;
                          const value = form.values[fieldName];

                          if (isCurrentSectionReadOnly) {
                            return (
                              <Stack key={`${repeatingQuestion.id}_${index}`} spacing={4}>
                                <Text size="sm" color="dimmed">
                                  {repeatingQuestion.conditional_logic?.substitute_index 
                                    ? repeatingQuestion.text.replace('[first]', getOrdinalWord(index + 1))
                                    : `${repeatingQuestion.text} (${index + 1})`}
                                </Text>
                                <Text>{formatValue(value, repeatingQuestion.type)}</Text>
                              </Stack>
                            );
                          }

                          const commonProps = {
                            label: repeatingQuestion.conditional_logic?.substitute_index 
                              ? repeatingQuestion.text.replace('[first]', getOrdinalWord(index + 1))
                              : `${repeatingQuestion.text} (${index + 1})`,
                            required: repeatingQuestion.required,
                            error: form.errors[fieldName],
                            ...getInputPropsWithDefaults(fieldName, repeatingQuestion.type),
                            styles: {
                              label: {
                                marginBottom: '8px'
                              }
                            }
                          };

                          switch (repeatingQuestion.type) {
                            case 'Text':
                              return repeatingQuestion.validation?.multiline ? (
                                <Textarea
                                  key={`${repeatingQuestion.id}_${index}`}
                                  {...commonProps}
                                  minRows={3}
                                  maxRows={6}
                                />
                              ) : (
                                <TextInput
                                  key={`${repeatingQuestion.id}_${index}`}
                                  {...commonProps}
                                />
                              );
                            case 'Number':
                              return (
                                <NumberInput
                                  key={`${repeatingQuestion.id}_${index}`}
                                  {...commonProps}
                                  min={repeatingQuestion.validation?.min_value}
                                  hideControls
                                  formatter={numberFormatter}
                                  parser={numberParser}
                                  styles={{
                                    root: { width: '100%' },
                                    input: { width: '300px' }
                                  }}
                                />
                              );
                            case 'Currency':
                              return (
                                <NumberInput
                                  key={`${repeatingQuestion.id}_${index}`}
                                  {...commonProps}
                                  min={repeatingQuestion.validation?.min_value}
                                  hideControls
                                  formatter={currencyFormatter}
                                  parser={currencyParser}
                                  styles={{
                                    root: { width: '100%' },
                                    input: { width: '300px' }
                                  }}
                                />
                              );
                            case 'SingleSelect':
                              return (
                                <Select
                                  key={`${repeatingQuestion.id}_${index}`}
                                  {...commonProps}
                                  data={repeatingQuestion.options || []}
                                  clearable
                                  styles={{
                                    root: { width: '300px' },
                                    label: {
                                      marginBottom: '8px'
                                    }
                                  }}
                                />
                              );
                            case 'MultiSelect':
                              return (
                                <MultiSelect
                                  key={`${repeatingQuestion.id}_${index}`}
                                  {...commonProps}
                                  data={repeatingQuestion.options || []}
                                  searchable
                                  creatable={repeatingQuestion.allow_other}
                                  maxSelectedValues={repeatingQuestion.validation?.max_selections}
                                  styles={{
                                    root: { width: '300px' },
                                    label: {
                                      marginBottom: '8px'
                                    }
                                  }}
                                />
                              );
                            case 'Date':
                              return (
                                <DateInput
                                  key={`${repeatingQuestion.id}_${index}`}
                                  {...commonProps}
                                  placeholder="Select a date"
                                  weekendDays={[]}
                                  styles={{
                                    root: { width: '300px' },
                                    label: {
                                      marginBottom: '8px'
                                    }
                                  }}
                                />
                              );
                            default:
                              return null;
                          }
                        })}
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Stack>
            );
          } else if (!question.conditional_logic?.repeat_based_on) {
            // This is a regular question that doesn't control repeating questions
            renderedQuestions.add(question.id);
            return renderQuestion(question);
          }
          
          return null;
        })}
      </Stack>
    );
  };

  const formatAnswersForSave = (values: FormValues): ReportAnswer[] => {
    const answers: ReportAnswer[] = [];
    
    // Get current repeat counts for all repeat controllers
    const repeatCounts: Record<number, number> = {};
    if (report?.template?.questions) {
      report.template.questions.forEach(question => {
        const hasRepeatingQuestions = report.template.questions.some(q => 
          q.conditional_logic?.repeat_based_on === question.id
        );
        if (hasRepeatingQuestions) {
          const currentCount = Number(values[`question_${question.id}`]) || 0;
          repeatCounts[question.id] = Math.min(Math.max(0, currentCount), MAX_REPEAT_COUNT);
        }
      });
    }
    
    const childQuestionIdsBlockedByZeroParent = new Set<number>();
    if (report?.template?.questions) {
      report.template.questions.forEach(question => {
        const parentId = question.validation?.max_value_from_question;
        if (parentId === undefined) return;

        if (isExplicitZeroValue(values[`question_${parentId}`])) {
          childQuestionIdsBlockedByZeroParent.add(question.id);
        }
      });
    }

    const hiddenShowIfQuestionIds = report?.template?.questions
      ? getHiddenShowIfQuestionIds(values, report.template.questions)
      : new Set<number>();

    // Process each form value
    Object.entries(values).forEach(([key, value]) => {
      // Check if this is a repeating question (has an index)
      const repeatMatch = key.match(/^question_(\d+)_(\d+)$/);
      if (repeatMatch) {
        // This is a repeating question
        const [, questionId, repeatIndex] = repeatMatch;
        const questionIdNum = parseInt(questionId);
        const repeatIndexNum = parseInt(repeatIndex);

        if (childQuestionIdsBlockedByZeroParent.has(questionIdNum)) {
          return;
        }
        if (hiddenShowIfQuestionIds.has(questionIdNum)) {
          return;
        }
        
        // Find the question to get its repeat_based_on value
        const question = report?.template?.questions.find(q => q.id === questionIdNum);
        if (question?.conditional_logic?.repeat_based_on) {
          const maxRepeatCount = repeatCounts[question.conditional_logic.repeat_based_on] || 0;
          
          // Only include this answer if it's within the current repeat count
          if (repeatIndexNum < maxRepeatCount) {
            // Convert Date objects to local calendar date strings for storage.
            let processedValue = value;
            if (value instanceof Date) {
              processedValue = formatDateForApi(value);
            }
            
            answers.push({
              question_id: questionIdNum,
              value: processedValue,
              repeat_index: repeatIndexNum
            });
          }
        }
      } else {
        // This is a regular question
        const questionMatch = key.match(/^question_(\d+)$/);
        if (questionMatch) {
          const [, questionId] = questionMatch;
          const questionIdNum = parseInt(questionId);

          if (childQuestionIdsBlockedByZeroParent.has(questionIdNum)) {
            return;
          }
          if (hiddenShowIfQuestionIds.has(questionIdNum)) {
            return;
          }
          
          // Convert Date objects to local calendar date strings for storage.
          let processedValue = value;
          if (value instanceof Date) {
            processedValue = formatDateForApi(value);
          }
          
          answers.push({
            question_id: questionIdNum,
            value: processedValue
          });
        }
      }
    });

    return answers;
  };

  const validateCurrentSection = () => {
    const currentSection = report?.template.sections[active];
    if (!currentSection) return {};

    const sectionQuestions = report?.template.questions.filter(q => 
      currentSection.question_ids.includes(q.id)
    ) || [];

    return getQuestionValidationErrors(form.values, sectionQuestions, true);
  };


  const restoreOriginalValues = () => {
    if (report?.answers) {
      const originalValues = report.answers.reduce((acc, answer) => {
        if (answer.repeat_index !== null && answer.repeat_index !== undefined) {
          acc[`question_${answer.question_id}_${answer.repeat_index}`] = answer.value;
        } else {
          acc[`question_${answer.question_id}`] = answer.value;
        }
        return acc;
      }, {} as Record<string, any>);
      
      form.setValues(originalValues);
      form.resetDirty();
      form.resetTouched();
    }
  };

  const saveFormProgress = async () => {
    try {
      // Clear any previous error when starting save operation
      setError(null);
      // Only include answers that have values (but 0 is a valid value for numbers)
      const answersWithValues = Object.entries(form.values).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value;
        } else if (value === 0) {
          // 0 is a valid value for number fields
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, any>);

      const formattedAnswers = formatAnswersForSave(answersWithValues);
      await saveAnswersMutation.mutateAsync(formattedAnswers);
    } catch (err) {
      // Don't show notification here - let the calling function handle error messages
      throw err;
    }
  };

  // Toggle full report view with unsaved changes check
  const handleToggleFullReportView = (checked: boolean) => {
    if (checked && form.isDirty() && !isCurrentSectionReadOnly) {
      // Show unsaved changes modal when switching to full report view with unsaved changes
      setShowNavigationModal(true);
      setPendingNavigation('full-report-view');
    } else {
      setIsFullReportView(checked);
    }
  };

  const handleStepClick = async (stepIndex: number) => {
    if (isCurrentSectionReadOnly) {
      setActive(stepIndex);
      // Reset edit mode when changing sections for pending_review reports
      if (report?.submission_status.state === 'pending_review') {
        setIsEditingCurrentSection(false);
      }
      window.scrollTo(0, 0);
      return;
    }

    // Get the current section and its questions
    const currentSection = report?.template.sections[active];
    if (!currentSection) {
      setActive(stepIndex);
      return;
    }

    const isGoingBackward = stepIndex < active;

    // Check if the current section has any data
    const sectionQuestions = report?.template.questions.filter(q => 
      currentSection.question_ids.includes(q.id)
    ) || [];

    const hasAnyData = sectionQuestions.some(question => {
      if (question.conditional_logic?.repeat_based_on) {
        const repeatCount = Number(form.values[`question_${question.conditional_logic.repeat_based_on}`]) || 0;
        for (let i = 0; i < repeatCount; i++) {
          const repeatFieldName = `question_${question.id}_${i}`;
          const repeatValue = form.values[repeatFieldName];
          if (repeatValue !== undefined && repeatValue !== null && repeatValue !== '') {
            return true;
          }
        }
        return false;
      } else {
        const fieldName = `question_${question.id}`;
        const value = form.values[fieldName];
        return value !== undefined && value !== null && value !== '';
      }
    });

    // If going backward and section is empty, allow navigation without validation
    if (isGoingBackward && !hasAnyData) {
      setActive(stepIndex);
      // Reset edit mode when changing sections for pending_review reports
      if (report?.submission_status.state === 'pending_review') {
        setIsEditingCurrentSection(false);
      }
      window.scrollTo(0, 0);
      return;
    }

    // For forward navigation or backward navigation with data, validate the section
    const sectionErrors = validateCurrentSection();

    // If there are validation errors and we're going forward, block navigation
    if (Object.keys(sectionErrors).length > 0 && !isGoingBackward) {
      // Set only the section-specific errors on the form
      form.setErrors(sectionErrors);
      showErrorNotification('Please correct the validation errors before proceeding.');
      return;
    }

    // For backward navigation with validation errors, save what we can and proceed
    // For forward navigation or backward navigation without errors, save and proceed
    try {
      // Clear any previous error when starting step navigation
      setError(null);
      if (hasAnyData) {
        await saveFormProgress();
      }
      setActive(stepIndex);
      // Reset edit mode when changing sections for pending_review reports
      if (report?.submission_status.state === 'pending_review') {
        setIsEditingCurrentSection(false);
      }
      window.scrollTo(0, 0); // Scroll to top of the new section
    } catch (err) {
      showApiErrorNotification(err, 'Failed to save progress. Please try again.');
      setError('Failed to save progress. Please try again.');
    }
  };

  const handleSubmit = async (values: FormValues) => {
    if (active === report?.template.sections.length) { // File management step
      try {
        setError(null);
        
        // First, save the form answers
        const answersWithValues = Object.entries(values).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            acc[key] = value;
          } else if (value === 0) {
            // 0 is a valid value for number fields
            acc[key] = value;
          }
          return acc;
        }, {} as Record<string, any>);

        const formattedAnswers = formatAnswersForSave(answersWithValues);
        await saveAnswersMutation.mutateAsync(formattedAnswers);
        
        // Second, update the status to pending_review
        await updateStatusMutation.mutateAsync('pending_review');
        
        // Reset form state to prevent navigation blocker
        form.resetDirty();
        setIsSavingAndExiting(true); // Prevent navigation blocker
        
        showSuccessNotification('Report has been successfully submitted for review');
        
        // Navigate to home page after successful submission
        navigate('/', { replace: true });
        
        // Don't reset flags here - let navigation complete
      } catch (err) {
        showApiErrorNotification(err, 'Failed to submit report. Please try again.');
        setError('Failed to submit report. Please try again.');
        setIsSavingAndExiting(false); // Reset flag on error
      }
    } else {
      await handleStepClick(active + 1);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  const handleNavigationLeaveWithoutSaving = () => {
    if (pendingNavigation === 'full-report-view') {
      // Switch to full report view without saving - restore original values
      restoreOriginalValues();
      setIsFullReportView(true);
      setShowNavigationModal(false);
      setPendingNavigation(null);
    } else {
      // Normal browser navigation - restore original values
      restoreOriginalValues();
      blocker.proceed?.();
      setShowNavigationModal(false);
      setPendingNavigation(null);
    }
  };

  const handleSaveAndExit = async () => {
    // For pending review reports in edit mode, validate the current section
    if (report?.submission_status.state === 'pending_review' && isEditingCurrentSection) {
      const sectionErrors = validateCurrentSection();
      
      if (Object.keys(sectionErrors).length > 0) {
        form.setErrors(sectionErrors);
        showErrorNotification('Please correct the validation errors before saving.');
        return;
      }
    }

    try {
      // Clear any previous error when starting save and exit
      setError(null);
      setIsSavingAndExiting(true);
      
      // Use the same logic as Save & Continue (handleStepClick)
      await saveFormProgress();
      form.resetDirty();
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      
      // Navigate after successful save
      navigate('/', { replace: true });
    } catch (err) {
      showApiErrorNotification(err, 'Failed to save progress. Please try again.');
      setError('Failed to save progress. Please try again.');
    } finally {
      setIsSavingAndExiting(false);
    }
  };

  const handleNavigationSaveAndExit = async () => {
    // For pending review reports in edit mode, validate the current section before saving
    if (report?.submission_status.state === 'pending_review' && isEditingCurrentSection) {
      const sectionErrors = validateCurrentSection();
      
      if (Object.keys(sectionErrors).length > 0) {
        form.setErrors(sectionErrors);
        showErrorNotification('Please correct the validation errors before saving.');
        setShowNavigationModal(false); // Close modal to show validation errors
        return;
      }
    }

    try {
      // Clear any previous error when starting navigation save
      setError(null);
      setIsSavingAndExiting(true);
      await saveFormProgress();
      form.resetDirty();
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      
      if (pendingNavigation === 'full-report-view') {
        // Switch to full report view after saving
        setIsFullReportView(true);
        setShowNavigationModal(false);
        setPendingNavigation(null);
      } else if (pendingNavigation) {
        // Normal browser navigation
        window.scrollTo(0, 0);
        blocker.proceed?.();
        setShowNavigationModal(false);
        setPendingNavigation(null);
      }
    } catch (err) {
      showApiErrorNotification(err, 'Failed to save progress. Please try again.');
      setError('Failed to save progress. Please try again.');
    } finally {
      setIsSavingAndExiting(false);
    }
  };

  const handleEditSection = () => {
    setIsEditingCurrentSection(true);
  };

  const handleCancelEdit = () => {
    setIsEditingCurrentSection(false);
    // Reset form values to their original state
    restoreOriginalValues();
  };



  // Simplified PDF Document Component  
  const ReportPDFDocument = ({ report, formValues, formatValue, getQuestionNumber }: any) => {
    const currentDate = dayjs().format('MMMM D, YYYY');
    const dueDateFormatted = dayjs(report.due_date).format('MMMM D, YYYY');
    const submittedAt = report.metadata?.submitted_at ? 
      dayjs(report.metadata.submitted_at).format('MMMM D, YYYY') : null;
    const submittedBy = report.metadata?.submitted_by || null;

    return (
      <Document>
        <Page size="LETTER" style={{ padding: 30, fontSize: 11, fontFamily: 'Helvetica' }}>
          {/* Header */}
          <PDFView style={{ 
            textAlign: 'center', 
            marginBottom: 30, 
            padding: 0, 
            backgroundColor: '#F7FAFC',
            borderWidth: 2,
            borderColor: '#3182CE',
            borderStyle: 'solid'
          }}>
            {/* Accent bar */}
            <PDFView style={{ 
              height: 6, 
              backgroundColor: '#3182CE', 
              marginBottom: 20 
            }} />
            
            <PDFView style={{ padding: 20 }}>
              <PDFText style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 10 }}>
                {getReportDisplayTitle(report) || 'Report'}
              </PDFText>
            <PDFText style={{ fontSize: 10, color: '#666' }}>Due Date: {dueDateFormatted}</PDFText>
            <PDFText style={{ fontSize: 10, color: '#666' }}>Generated: {currentDate}</PDFText>
            <PDFText style={{ fontSize: 10, color: '#666' }}>Report ID: {report.id}</PDFText>
            <PDFText style={{ fontSize: 10, color: '#666' }}>Status: {report.submission_status?.state?.replace('_', ' ')?.toUpperCase() || 'DRAFT'}</PDFText>
            {submittedAt && (
              <PDFText style={{ fontSize: 10, color: '#666' }}>Submitted: {submittedAt}</PDFText>
            )}
            {submittedBy && (
              <PDFText style={{ fontSize: 10, color: '#666' }}>Submitted By: {submittedBy}</PDFText>
            )}
            </PDFView>
          </PDFView>

          {/* Sections */}
          {report.template?.sections?.map((section: any, sectionIndex: number) => {
            const sectionQuestions = section.question_ids
              ?.map((qId: number) => report.template.questions?.find((q: any) => q.id === qId))
              .filter((q: any) => q !== undefined) || [];

            return (
              <PDFView key={section.id} style={{ marginBottom: 25, padding: 15, backgroundColor: '#FAFAFA' }}>
                <PDFText style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 10, color: '#2D3748' }}>
                  Section {sectionIndex + 1}: {section.title || 'Untitled Section'}
                </PDFText>
                
                {section.description && (
                  <PDFText style={{ fontSize: 10, fontStyle: 'italic', color: '#666', marginBottom: 15 }}>
                    {section.description}
                  </PDFText>
                )}

                {buildPdfRenderableQuestions({
                  sectionQuestions,
                  formValues,
                  getQuestionNumber,
                  formatValue,
                  getOrdinalWord,
                  maxRepeatCount: MAX_REPEAT_COUNT,
                }).map((question) => (
                  <PDFView key={question.key} style={{ marginBottom: 15, padding: 10, backgroundColor: '#FFFFFF' }}>
                    <PDFText style={{ fontSize: 11, fontWeight: 'bold', marginBottom: 5 }}>
                      {question.questionNumber}. {question.label}
                    </PDFText>
                    <PDFText style={{ fontSize: 10, color: '#4A5568', marginBottom: question.repeatedItems?.length ? 8 : 0 }}>
                      {question.value}
                    </PDFText>

                    {question.repeatedItems && question.repeatedItems.length > 0 && (
                      <PDFView style={{ paddingLeft: 10 }}>
                        {question.repeatedItems.map((item) => (
                          <PDFView
                            key={item.key}
                            style={{ marginBottom: 8, padding: 8, backgroundColor: '#F8FAFC' }}
                          >
                            <PDFText style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 5, color: '#2D3748' }}>
                              {item.itemLabel}
                            </PDFText>
                            {item.answers.map((answer) => (
                              <PDFView key={answer.key} style={{ marginBottom: 4 }}>
                                <PDFText style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 2 }}>
                                  {answer.label}
                                </PDFText>
                                <PDFText style={{ fontSize: 10, color: '#4A5568' }}>
                                  {answer.value}
                                </PDFText>
                              </PDFView>
                            ))}
                          </PDFView>
                        ))}
                      </PDFView>
                    )}
                  </PDFView>
                ))}
              </PDFView>
            );
          })}

          {/* Supporting Documents Section */}
          <PDFView style={{ marginBottom: 25, padding: 15, backgroundColor: '#FAFAFA' }}>
            <PDFText style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 10, color: '#2D3748' }}>
              Supporting Documents
            </PDFText>
            <PDFText style={{ fontSize: 10, fontStyle: 'italic', color: '#666', marginBottom: 15 }}>
              Uploaded files and attachments related to this report
            </PDFText>
            
            {report.files && report.files.length > 0 ? (
              report.files.map((file: any, index: number) => (
                <PDFView key={index} style={{ marginBottom: 10, padding: 10, backgroundColor: '#FFFFFF' }}>
                  <PDFText style={{ fontSize: 11, fontWeight: 'bold', marginBottom: 3 }}>
                    {file.filename || file.name || `Document ${index + 1}`}
                  </PDFText>
                  <PDFText style={{ fontSize: 10, color: '#4A5568' }}>
                    {file.description || 'No description provided'}
                  </PDFText>
                  {file.uploadedAt && (
                    <PDFText style={{ fontSize: 9, color: '#666', fontStyle: 'italic' }}>
                      Uploaded: {dayjs(file.uploadedAt).format('MMMM D, YYYY')}
                    </PDFText>
                  )}
                </PDFView>
              ))
            ) : (
              <PDFView style={{ padding: 15, backgroundColor: '#F8F9FA', textAlign: 'center' }}>
                <PDFText style={{ fontSize: 11, fontStyle: 'italic', color: '#6C757D' }}>
                  No supporting documents have been uploaded for this report.
                </PDFText>
              </PDFView>
            )}
          </PDFView>

          {/* Footer */}
          <PDFView style={{ marginTop: 30, paddingTop: 15, textAlign: 'center' }}>
            <PDFText style={{ fontSize: 9, color: '#666', fontStyle: 'italic' }}>
              This document was generated electronically on {currentDate}
            </PDFText>
          </PDFView>
        </Page>
      </Document>
    );
  };

  // Download report as PDF
  const handleDownloadPDF = async () => {
    if (!report) {
      showErrorNotification('Report data not available for PDF generation');
      return;
    }

    setIsGeneratingPDF(true);
    
    try {
      // Create filename from report title (same as cards) and due date
      const reportName = getReportDisplayTitle(report).replace(/[^a-z0-9]/gi, '_');
      const dueDate = dayjs(report.due_date).format('YYYY-MM-DD');
      const filename = `${reportName}_${dueDate}.pdf`;

      // Generate PDF
      const blob = await pdf(
        <ReportPDFDocument 
          report={report}
          formValues={form.values}
          formatValue={formatValue}
          getQuestionNumber={getQuestionNumber}
        />
      ).toBlob();

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      showSuccessNotification(`PDF downloaded: ${filename}`);
      
    } catch (error) {
      console.error('PDF generation failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Error details:', errorMessage);
      showErrorNotification(`Failed to generate PDF: ${errorMessage}. Check console for details.`);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Block navigation when form has actual changes and we're not saving and exiting
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => {
      const shouldBlock = !isCurrentSectionReadOnly && hasFormChanges && !isSavingAndExiting && !(saveAnswersMutation.isPending || updateStatusMutation.isPending) && currentLocation.pathname !== nextLocation.pathname;
      return shouldBlock;
    }
  );

  // Handle blocked navigation
  useEffect(() => {
    if (blocker.state === "blocked") {
      setPendingNavigation(blocker.location.pathname);
      setShowNavigationModal(true);
    }
  }, [blocker]);

  // Render all sections for full report view (web interface only)
  const renderFullReport = () => {
    if (!report?.template?.sections) return null;

    return (
      <Stack spacing="lg" id="full-report-content">
        <Alert color="blue" variant="light">
          <Group position="apart" align="center">
            <Text size="sm">
              <strong>Full Report View:</strong> This is a read-only view showing all questions and current answers. Switch back to step-by-step mode to make changes.
            </Text>
            <ActionButton
              onClick={() => setIsFullReportView(false)}
              label="Exit Full View"
              actionType="secondary"
              ariaLabel="Exit full report view and return to step-by-step navigation"
            />
          </Group>
        </Alert>
        
        <ScrollArea style={{ height: '65vh' }} data-scroll-area="true">
          <Stack spacing="xl">
          {report.template.sections.map((section: ReportSection, index: number) => (
            <Paper key={section.id} p="md" withBorder sx={(theme) => ({
              backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
              borderLeft: `4px solid ${theme.colors.blue[6]}`
            })}>
              <Stack spacing="md">
                <Group position="apart" align="center">
                  <Title order={3} color="blue.6">
                    {section.title}
                  </Title>
                </Group>
                <Text color="dimmed" size="sm">{section.description}</Text>
                <Divider />
                
                {/* Render section in read-only mode */}
                <div style={{ pointerEvents: 'none' }}>
                  {renderSection(section.id, true)}
                </div>
              </Stack>
            </Paper>
          ))}
          
          {/* Supporting Documents */}
          <Paper p="md" withBorder sx={(theme) => ({
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
            borderLeft: `4px solid ${theme.colors.blue[6]}`
          })}>
            <Stack spacing="md">
              <Group position="apart" align="center">
                <Title order={3} color="blue.6">
                  Supporting Documents
                </Title>
              </Group>
              <Text color="dimmed" size="sm">Upload supporting documents, attachments, or evidence related to this report.</Text>
              <Divider />
              
              <div style={{ pointerEvents: 'none' }}>
                <FileManagement
                  reportId={reportId || ''}
                  showHeader={false}
                  isReadOnly={true}
                />
              </div>
            </Stack>
          </Paper>
        </Stack>
      </ScrollArea>
    </Stack>
    );
  };

  return (
    <>
      <Container size="xl" mt="xl" pb="xl" px={0}>
        <Paper shadow="sm" p="xl" radius="md" sx={(theme) => ({
          '@media (max-width: 768px)': {
            padding: theme.spacing.md,
            margin: theme.spacing.sm,
          }
        })}>
          <Stack spacing="xl">
            {isLoading ? (
              <>
                {/* Header Skeleton */}
                <Stack spacing="xs">
                  <Group position="apart" align="center">
                    <Skeleton height={32} width="60%" radius="md" />
                    <Skeleton height={20} width="30%" radius="md" />
                  </Group>
                  <Skeleton height={16} width="80%" radius="md" />
                  <Skeleton height={24} width="25%" radius="md" />
                </Stack>

                {/* Stepper Skeleton */}
                <Stack spacing="md">
                  <Group spacing="md">
                    <Skeleton height={8} width={80} radius="md" />
                    <Skeleton height={8} width={80} radius="md" />
                    <Skeleton height={8} width={80} radius="md" />
                  </Group>
                </Stack>

                {/* Content Skeleton */}
                <Stack spacing="md">
                  <Skeleton height={40} radius="md" />
                  <Stack spacing="md">
                    <Skeleton height={24} width="40%" radius="md" />
                    <Skeleton height={20} width="70%" radius="md" />
                    <Stack spacing="sm">
                      <Skeleton height={36} radius="md" />
                      <Skeleton height={36} radius="md" />
                      <Skeleton height={36} radius="md" />
                    </Stack>
                  </Stack>
                </Stack>

                {/* Action Buttons Skeleton */}
                <Group position="apart" mt="xl" sx={(theme) => ({
                  '@media (max-width: 768px)': {
                    flexDirection: 'column',
                    gap: theme.spacing.md,
                    alignItems: 'stretch'
                  }
                })}>
                  <Group sx={() => ({
                    '@media (max-width: 768px)': {
                      justifyContent: 'center'
                    }
                  })}>
                    <Skeleton height={36} width={120} radius="md" />
                    <Skeleton height={36} width={120} radius="md" />
                  </Group>
                  <Group sx={() => ({
                    '@media (max-width: 768px)': {
                      justifyContent: 'center'
                    }
                  })}>
                    <Skeleton height={36} width={120} radius="md" />
                    <Skeleton height={36} width={120} radius="md" />
                  </Group>
                </Group>
              </>
            ) : queryError ? (
              <>
                <Alert 
                  icon={<IconAlertCircle size={16} />} 
                  title="Failed to Load Report" 
                  color="red"
                  role="alert"
                >
                  {queryError instanceof Error ? queryError.message : 'An error occurred while loading the report. Please try refreshing the page or contact support if the problem persists.'}
                </Alert>
                <Group mt="md" sx={() => ({
                  '@media (max-width: 768px)': {
                    flexDirection: 'column',
                    alignItems: 'stretch'
                  }
                })}>
                  <ActionButton
                    onClick={() => window.location.reload()}
                    label="Retry"
                    ariaLabel="Reload the page to try again"
                    actionType="primary"
                  />
                  <ActionButton
                    onClick={() => navigate('/reports')}
                    label="Back to Reports"
                    ariaLabel="Return to reports list"
                    actionType="secondary"
                  />
                </Group>
              </>
            ) : (
              <>
                {error && (
                  <Alert 
                    icon={<IconAlertCircle size={16} />} 
                    title="Error" 
                    color="red"
                    role="alert"
                  >
                    {error}
                  </Alert>
                )}

                {report && (
                  <>
                    <Stack spacing="xs">
                      <Group position="apart" align="center" sx={(theme) => ({
                        '@media (max-width: 768px)': {
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          gap: theme.spacing.xs
                        }
                      })}>
                        <Group spacing="xs">
                          <Title order={2} sx={(theme) => ({
                            '@media (max-width: 768px)': {
                              fontSize: theme.fontSizes.lg
                            }
                          })}>{getReportDisplayTitle(report)}</Title>
                          {report.metadata?.updated_by && (
                            <Tooltip
                              label={
                                <Stack spacing={0}>
                                  {report.metadata?.submitted_at && (
                                    <>
                                      <Text size="xs">
                                        submitted {formatTimestampToLocal(report.metadata.submitted_at)}
                                      </Text>
                                      {report.metadata?.submitted_by && (
                                        <Text size="xs">
                                          by {report.metadata.submitted_by}
                                        </Text>
                                      )}
                                      <Divider my={4} />
                                    </>
                                  )}
                                  <Text size="xs">
                                    last updated {formatTimestampToLocal(report.metadata.updated_at)}
                                  </Text>
                                  <Text size="xs">
                                    by {report.metadata.updated_by}
                                  </Text>
                                </Stack>
                              }
                              position="right"
                              withArrow
                            >
                              <IconInfoCircle size={16} style={{ color: 'var(--mantine-color-dimmed)' }} />
                            </Tooltip>
                          )}
                        </Group>
                        <Text weight={500} color="blue.7" sx={(theme) => ({
                          '@media (max-width: 768px)': {
                            fontSize: theme.fontSizes.sm
                          }
                        })}>
                          Due: {dayjs(report.due_date).format('MMMM D, YYYY')}
                        </Text>
                      </Group>
                      <Text color="dimmed">{report.template.description}</Text>
                      <Group position="apart" align="center">
                        <StatusBadge status={mapReportStatus(report.submission_status.state, report.due_date)} />
                        <Group spacing="md">
                          <Group spacing="sm">
                            <Text size="sm" color="dimmed">Full Report View</Text>
                            <Switch
                              checked={isFullReportView}
                              onChange={(event) => handleToggleFullReportView(event.currentTarget.checked)}
                              size="sm"
                              onLabel="ON"
                              offLabel="OFF"
                            />
                          </Group>
                          <Menu shadow="md" width={240} position="bottom-end">
                            <Menu.Target>
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                size="xl"
                                aria-label="Report actions menu"
                              >
                                <IconDots size={24} />
                              </ActionIcon>
                            </Menu.Target>

                            <Menu.Dropdown>
                              <Menu.Label>Report actions</Menu.Label>
                              <Menu.Item
                                icon={<IconDownload size={14} />}
                                onClick={handleDownloadPDF}
                                disabled={isGeneratingPDF || saveAnswersMutation.isPending || updateStatusMutation.isPending || isSavingAndExiting}
                              >
                                {isGeneratingPDF ? 'Generating Report...' : 'Download Report'}
                              </Menu.Item>
                              {isAdmin &&
                                (report.submission_status.state === 'pending_review' ||
                                  report.submission_status.state === 'in_review') && (
                                  <>
                                    <Menu.Divider />
                                    <Menu.Label>Review workflow</Menu.Label>
                                    {report.submission_status.state === 'pending_review' && (
                                      <Menu.Item
                                        icon={<IconClipboardList size={14} />}
                                        onClick={() => setAdminStartReviewModalOpen(true)}
                                        disabled={updateStatusMutation.isPending}
                                        aria-label="Open start review confirmation"
                                      >
                                        Start review
                                      </Menu.Item>
                                    )}
                                    {report.submission_status.state === 'in_review' && (
                                      <>
                                        <Menu.Item
                                          icon={<IconCircleCheck size={14} />}
                                          onClick={() => setAdminApproveModalOpen(true)}
                                          disabled={updateStatusMutation.isPending}
                                          aria-label="Open approve report confirmation"
                                        >
                                          Approve
                                        </Menu.Item>
                                        <Menu.Item
                                          icon={<IconArrowBackUp size={14} />}
                                          color="red"
                                          onClick={() => setAdminRejectModalOpen(true)}
                                          disabled={updateStatusMutation.isPending}
                                          aria-label="Open reject report confirmation"
                                        >
                                          Reject
                                        </Menu.Item>
                                      </>
                                    )}
                                  </>
                                )}
                            </Menu.Dropdown>
                          </Menu>
                        </Group>
                      </Group>
                    </Stack>

                    {isAdmin && (
                      <AdminReviewModals
                        startReviewOpen={adminStartReviewModalOpen}
                        approveOpen={adminApproveModalOpen}
                        rejectOpen={adminRejectModalOpen}
                        onCloseStartReview={() => setAdminStartReviewModalOpen(false)}
                        onCloseApprove={() => setAdminApproveModalOpen(false)}
                        onCloseReject={() => setAdminRejectModalOpen(false)}
                        onConfirmStartReview={() => void handleAdminStartReview()}
                        onConfirmApprove={handleAdminApprove}
                        onConfirmReject={handleAdminReject}
                      />
                    )}

                    <form onSubmit={form.onSubmit(handleSubmit)}>
                      {isFullReportView ? (
                        // Full Report View - show all sections at once
                        renderFullReport()
                      ) : isCurrentSectionReadOnly ? (
                        // Custom read-only navigation with buttons
                        <Stack spacing="xl">
                          <Group spacing="xs" mb="xl" sx={() => ({
                            '@media (max-width: 768px)': {
                              flexDirection: 'column',
                              alignItems: 'stretch'
                            }
                          })}>
                            {report.template.sections.map((section: ReportSection, index: number) => (
                              <ActionButton
                                key={section.id}
                                onClick={() => setActive(index)}
                                label={section.title}
                                actionType={index === active ? "primary" : "secondary"}
                                ariaLabel={`Go to ${section.title} section`}
                              />
                            ))}
                            {/* Add Supporting Documents button for read-only mode */}
                            <ActionButton
                              key="file-management-readonly"
                              onClick={() => setActive(report.template.sections.length)}
                              label="Supporting Documents"
                              actionType={report.template.sections.length === active ? "primary" : "secondary"}
                              ariaLabel="Go to Supporting Documents section"
                            />
                          </Group>
                          
                          {/* Render the current section content */}
                          {active < report.template.sections.length ? (
                            renderSection(report.template.sections[active].id)
                          ) : (
                            // File Management step for read-only mode
                            <FileManagement
                              reportId={reportId || ''}
                              isReadOnly={isCurrentSectionReadOnly}
                            />
                          )}
                        </Stack>
                      ) : (
                        // Regular Stepper for edit mode
                        <Stack spacing="xl">
                          <Stepper 
                            active={active} 
                            breakpoint="sm"
                            aria-label="Report submission steps"
                            allowNextStepsSelect={true}
                            onStepClick={handleStepClick}
                            color="blue"
                            size="sm"
                            radius="md"
                            sx={(theme) => ({
                              // Allow horizontal scrolling if content overflows
                              overflowX: 'auto',
                              // Add padding to prevent content cutoff
                              paddingBottom: theme.spacing.xs,
                              // Ensure stepper items wrap properly on smaller screens
                              '& .mantine-Stepper-steps': {
                                flexWrap: 'nowrap',
                                minWidth: '100%', // Minimum full width
                                gap: '0', // Remove any default gaps to control spacing manually
                                display: 'flex',
                                justifyContent: 'space-evenly' // Equal spacing around each step including edges
                              },
                              // Control spacing for entire step element
                              '& .mantine-Stepper-step': {
                                display: 'flex',
                                alignItems: 'center',
                                flex: '0 0 auto', // Fixed size, don't grow or shrink
                                padding: '0 24px', // Fixed horizontal padding around entire step
                                '&:last-child .mantine-Stepper-separator': {
                                  display: 'none' // Hide separator after last step
                                }
                              },
                              // Ensure separator lines are visible
                              '& .mantine-Stepper-separator': {
                                display: 'block !important',
                                backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3],
                                height: '2px',
                                width: '32px', // Fixed width separator
                                margin: '0', // No margin - let padding handle spacing
                                borderRadius: '1px',
                                flex: '0 0 auto' // Fixed size, don't grow
                              },
                              // Style completed step separators
                              '& .mantine-Stepper-stepCompleted + .mantine-Stepper-separator': {
                                backgroundColor: theme.colors.blue[6]
                              },
                              // Style the icon container for consistent spacing
                              '& .mantine-Stepper-stepIcon': {
                                borderWidth: '2px',
                                borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3],
                                marginRight: '8px' // Small gap between icon and label
                              },
                              // Style the step content
                              '& .mantine-Stepper-stepBody': {
                                // Allow text wrapping in step labels
                                whiteSpace: 'normal',
                                // Set compact max width for step labels
                                maxWidth: '100px',
                                // Reduce line height for better compact display
                                lineHeight: 1.3,
                                // Remove margins - let separator handle all spacing
                                margin: 0
                              },
                              '& .mantine-Stepper-stepLabel': {
                                // Ensure labels wrap properly without breaking words
                                overflowWrap: 'break-word',
                                wordWrap: 'break-word',
                                wordBreak: 'normal',
                                hyphens: 'auto',
                                fontSize: theme.fontSizes.sm,
                                fontWeight: 500,
                                // Remove any default margins
                                margin: 0
                              },
                              // Responsive adjustments
                              '@media (max-width: 768px)': {
                                '& .mantine-Stepper-step': {
                                  padding: '0 18px' // Fixed horizontal padding for tablet
                                },
                                '& .mantine-Stepper-separator': {
                                  width: '24px',
                                  margin: '0', // No margin - let padding handle spacing
                                  flex: '0 0 auto'
                                },
                                '& .mantine-Stepper-stepIcon': {
                                  marginRight: '6px'
                                },
                                '& .mantine-Stepper-stepBody': {
                                  maxWidth: '80px' // Compact width for tablet
                                },
                                '& .mantine-Stepper-stepLabel': {
                                  fontSize: theme.fontSizes.xs
                                }
                              },
                              '@media (max-width: 480px)': {
                                '& .mantine-Stepper-step': {
                                  padding: '0 12px' // Fixed horizontal padding for mobile
                                },
                                '& .mantine-Stepper-separator': {
                                  width: '18px',
                                  margin: '0', // No margin - let padding handle spacing
                                  flex: '0 0 auto'
                                },
                                '& .mantine-Stepper-stepIcon': {
                                  marginRight: '4px'
                                },
                                '& .mantine-Stepper-stepBody': {
                                  maxWidth: '60px' // Very compact width for mobile
                                },
                                '& .mantine-Stepper-stepLabel': {
                                  fontSize: '10px'
                                }
                              }
                            })}
                          >
                            {report.template.sections.map((section: ReportSection) => (
                              <Stepper.Step 
                                key={section.id}
                                label={section.title}
                              />
                            ))}
                            {/* Add File Management step */}
                            <Stepper.Step 
                              key="file-management"
                              label="Supporting Documents"
                            />
                          </Stepper>

                          {/* Render the current section content */}
                          {active < report.template.sections.length ? (
                            renderSection(report.template.sections[active].id)
                          ) : (
                            // File Management step
                            <FileManagement
                              reportId={reportId || ''}
                              isReadOnly={isCurrentSectionReadOnly}
                            />
                          )}
                        </Stack>
                      )}

                      {isFullReportView ? (
                        // Simplified navigation for full report view - only Back to Reports button
                        <Group position="left" mt="xl">
                          <ActionButton
                            onClick={handleCancel}
                            label="Back to Reports"
                            ariaLabel="Return to reports list"
                            actionType="secondary"
                            disabled={saveAnswersMutation.isPending || updateStatusMutation.isPending || isSavingAndExiting}
                          />
                        </Group>
                      ) : (
                        // Full navigation for step-by-step view
                        <Group position="apart" mt="xl" sx={(theme) => ({
                          '@media (max-width: 768px)': {
                            flexDirection: 'column',
                            gap: theme.spacing.md,
                            alignItems: 'stretch'
                          }
                        })}>
                          <Group sx={() => ({
                            '@media (max-width: 768px)': {
                              justifyContent: 'center',
                              width: '100%'
                            }
                          })}>
                            <ActionButton
                              onClick={handleCancel}
                              label="Back to Reports"
                              ariaLabel="Return to reports list"
                              actionType="secondary"
                              disabled={saveAnswersMutation.isPending || updateStatusMutation.isPending || isSavingAndExiting}
                            />
                            {active > 0 && (
                              <ActionButton
                                onClick={() => handleStepClick(active - 1)}
                                label="Previous Section"
                                ariaLabel="Go to previous section"
                                actionType="secondary"
                                disabled={saveAnswersMutation.isPending || updateStatusMutation.isPending || isSavingAndExiting}
                              />
                            )}
                          </Group>
                          <Group sx={() => ({
                            '@media (max-width: 768px)': {
                              justifyContent: 'center',
                              width: '100%'
                            }
                          })}>
                            {!isCurrentSectionReadOnly && (
                              <ActionButton
                                onClick={handleSaveAndExit}
                                label="Save & Exit"
                                ariaLabel="Save progress and return to reports list"
                                actionType="secondary"
                                isLoading={isSavingAndExiting}
                                disabled={saveAnswersMutation.isPending || updateStatusMutation.isPending}
                              />
                            )}
                            {/* Show Edit button for pending_review when in read-only mode */}
                            {report?.submission_status.state === 'pending_review' && !isEditingCurrentSection && (
                              <ActionButton
                                onClick={handleEditSection}
                                label="Edit Section"
                                ariaLabel="Edit current section"
                                actionType="primary"
                              />
                            )}
                            {/* Show Cancel button when editing pending_review */}
                            {report?.submission_status.state === 'pending_review' && isEditingCurrentSection && (
                              <ActionButton
                                onClick={handleCancelEdit}
                                label="Cancel Edit"
                                ariaLabel="Cancel editing and return to read-only mode"
                                actionType="secondary"
                              />
                            )}
                            {!isCurrentSectionReadOnly ? (
                              <ActionButton
                                onClick={active === report.template.sections.length ? 
                                  () => {
                                    // On file management step, submit the report
                                    form.resetDirty();
                                    handleSubmit(form.values);
                                  } : 
                                  active === (report.template.sections.length || 0) - 1 ?
                                  () => handleStepClick(active + 1) : // Go to file management step
                                  () => {
                                    const sectionErrors = validateCurrentSection();
                                    if (Object.keys(sectionErrors).length === 0) {
                                      handleStepClick(active + 1);
                                    } else {
                                      form.setErrors(sectionErrors);
                                      showErrorNotification('Please correct the validation errors before proceeding.');
                                    }
                                  }
                                }
                                label={active === report.template.sections.length ? 'Submit Report' : 
                                       active === (report.template.sections.length || 0) - 1 ? 'Continue to Documents' : 
                                       'Save & Continue'}
                                ariaLabel={active === report.template.sections.length ? "Submit report" : 
                                          active === (report.template.sections.length || 0) - 1 ? "Continue to document upload" :
                                          "Save progress and continue"}
                                actionType="primary"
                                isLoading={active === report.template.sections.length ? updateStatusMutation.isPending : saveAnswersMutation.isPending}
                              />
                            ) : (
                              active < report.template.sections.length && !isFullReportView && (
                                <ActionButton
                                  onClick={() => handleStepClick(active + 1)}
                                  label={active === (report.template.sections.length || 0) - 1 ? "View Documents" : "Next Section"}
                                  ariaLabel={active === (report.template.sections.length || 0) - 1 ? "Go to document management" : "Go to next section"}
                                  actionType="primary"
                                />
                              )
                            )}
                          </Group>
                        </Group>
                      )}
                    </form>
                  </>
                )}
              </>
            )}
          </Stack>
        </Paper>
      </Container>

      <ConfirmationModal
        opened={showNavigationModal}
        onClose={() => setShowNavigationModal(false)}
        onCancel={handleNavigationLeaveWithoutSaving}
        onConfirm={handleNavigationSaveAndExit}
        title="Unsaved Changes"
        message="You have unsaved changes. Would you like to save your progress before leaving?"
        confirmLabel="Save & Exit"
        cancelLabel="Leave Without Saving"
        confirmAriaLabel="Save progress and return to reports list"
        cancelAriaLabel="Leave page and discard changes"
      />
    </>
  );
} 